'use client'

import { useCallback, useState } from 'react'
import { useOpenAuthModal } from '@/components/marketing/v2/use-auth-modal'
import { trackFreeToolEvent } from '@/lib/site/gtm'
import {
  discardAuditedEmailForBrew,
  downloadAuditedEmail,
  saveAuditedEmailForBrew,
} from './email-audit-handoff'
import { useEmailAuditCopy } from './use-email-audit-copy'

export type EmailAuditHandoffReason = 'rendering' | 'fix'

export type EmailAuditHandoff = {
  /** Save the email for Brew's chat, then open sign-in in place. */
  continueInBrew: (reason: EmailAuditHandoffReason) => Promise<void>
  isLeaving: boolean
  /** The browser refused to keep the email, so sign-in did not open. */
  isBlocked: boolean
  /** After a refusal: download the email to attach it in Brew by hand. */
  downloadEmail: () => void
  /**
   * After a refusal: open sign-in without the email, first dropping any
   * older audited email an earlier attempt left for Brew.
   */
  continueWithoutEmail: () => Promise<void>
}

/**
 * Every "continue in Brew" button on the free audit: save the email (when
 * there is one) for the chat, then open sign-in in place (a signed-in visitor
 * goes to `/home`). When the browser will not keep the email, sign-in waits
 * and the email stays on the page, ready to download.
 */
export function useEmailAuditHandoff(html: string | null): EmailAuditHandoff {
  const openAuth = useOpenAuthModal()
  // The prompt the email arrives with in Brew's chat, in the reader's
  // language, so the chat answers in it too.
  const { rendering } = useEmailAuditCopy()
  const [isLeaving, setIsLeaving] = useState(false)
  // The email the browser refused to keep. The notice belongs to that email
  // only: once the page audits another one, or none, it disappears.
  const [refusedHtml, setRefusedHtml] = useState<string | null>(null)
  const isBlocked = html !== null && refusedHtml === html

  const continueInBrew = useCallback(
    async (reason: EmailAuditHandoffReason) => {
      trackFreeToolEvent({
        tool: 'email-audit',
        action: reason === 'fix' ? 'fix_click' : 'upsell_click',
      })
      if (!html) {
        // No email here (a shared report): an audited email an earlier visit
        // left must not reappear in Brew as if it were this one.
        await discardAuditedEmailForBrew()
        openAuth()
        return
      }
      setIsLeaving(true)
      const isSaved = await saveAuditedEmailForBrew({
        html,
        prompt:
          reason === 'fix' ? rendering.fixPrompt : rendering.renderingPrompt,
      })
      setIsLeaving(false)
      setRefusedHtml(isSaved ? null : html)
      if (isSaved) {
        openAuth()
      }
    },
    [html, openAuth, rendering]
  )
  const downloadEmail = useCallback(() => {
    if (html) {
      downloadAuditedEmail(html)
    }
  }, [html])
  const continueWithoutEmail = useCallback(async () => {
    setRefusedHtml(null)
    await discardAuditedEmailForBrew()
    openAuth()
  }, [openAuth])

  return {
    continueInBrew,
    isLeaving,
    isBlocked,
    downloadEmail,
    continueWithoutEmail,
  }
}
